import { _IFlagged, _IManually } from '@data/types'

import { discountItemAmount } from '../../calculations/discount-item-amount/discount-item-amount'
import { findFirstMatchIndex } from '../find-first-match-index/find-first-match-index'

export const priorityReasonFlagItemList = [
  'productOutOfStock',
  'productNotSuitableForShipping',
]

type RemoveItemsFromArrayByAmountArgs<T extends _IFlagged | _IManually> = {
  list?: T[] | null
  amountToRemove: number
  initialScannedAmount: number
  priorityReasonList?: string[]
  onExceedCallback: ({
    remainingAmount,
    list,
    remainingScannedAmount,
  }: {
    remainingAmount: number
    list?: T[] | null
    remainingScannedAmount: number
  }) => void
  onError: (args: { remainingAmount: number; list?: T[] | null }) => void
}

export function removeItemsFromArrayByAmount<T extends _IFlagged | _IManually>({
  list,
  amountToRemove,
  initialScannedAmount,
  priorityReasonList = [],
  onExceedCallback,
  onError,
}: RemoveItemsFromArrayByAmountArgs<T>) {
  if (!list) {
    return {
      scannedAmount: 0,
      listAmount: 0,
      list: null,
    }
  }

  const reasonsAmount = list.reduce((total, item) => total + item.amount, 0)

  if (amountToRemove === reasonsAmount + initialScannedAmount) {
    return {
      scannedAmount: 0,
      listAmount: 0,
      list: null,
    }
  }

  let updatedList = [...list]
  let remainingAmountToRemove = amountToRemove
  let remainingScannedAmount = initialScannedAmount

  do {
    const priorityReasonIndex = findFirstMatchIndex(
      priorityReasonList,
      updatedList
    )

    if (updatedList.length) {
      const { itemsList, remainingAmount: result } = discountItemAmount({
        itemsList: updatedList,
        itemIndex: priorityReasonIndex,
        remainingAmount: remainingAmountToRemove,
      })

      remainingAmountToRemove = result
      updatedList = itemsList as T[]
    } else if (!updatedList.length && remainingAmountToRemove) {
      if (remainingAmountToRemove <= remainingScannedAmount) {
        remainingScannedAmount -= remainingAmountToRemove
        remainingAmountToRemove = 0
      } else {
        onExceedCallback({
          remainingAmount: remainingAmountToRemove,
          list,
          remainingScannedAmount,
        })
        break
      }
    } else {
      onError({
        remainingAmount: remainingAmountToRemove,
        list,
      })
      break
    }
  } while (remainingAmountToRemove > 0)

  const remainingListAmount = updatedList
    ? updatedList.reduce((total, item) => total + item.amount, 0)
    : 0

  return {
    scannedAmount: remainingScannedAmount,
    listAmount: remainingListAmount,
    list: updatedList.length ? updatedList : null,
  }
}
